// Which seasonal look the synchronization loader wears (#23635, eCOS_SpecTech_Loader-synchronisation v1.0, RG-11 to
// RG-26): the calendar of the themes for France and Germany, validated with marketing, and the client parameters
// set in the Back-Office. The visuals live in src/utils/ui/ecosShelfThemes.js.
// The calendar keeps neutral weeks between the highlights, except the year-end chain (Saint Nicholas, Christmas,
// New Year's Eve). Moving dates (carnival, Easter, the July cycling race) are computed every year; sport events
// are dated in SPORT_EVENTS, to complete as soon as the next editions are official (RG-20).

export const SEASONAL_THEMES = ['epiphanie', 'saint-valentin', 'carnaval', 'paques', 'rentree', 'halloween', 'saint-nicolas', 'noel', 'saint-sylvestre', 'cyclisme', 'football', 'jeux-ete', 'jeux-hiver'];

/** Dated sport events: they win over the yearly calendar during their dates (RG-15). */
export const SPORT_EVENTS = [
	{ theme: 'football', from: '2028-06-09', to: '2028-07-09', name: 'Euro 2028 (United Kingdom, Ireland)' },
	{ theme: 'jeux-ete', from: '2028-07-14', to: '2028-07-30', name: 'Summer games 2028 (Los Angeles)' },
	{ theme: 'jeux-hiver', from: '2030-02-01', to: '2030-02-17', name: 'Winter games 2030 (French Alps)' },
	{ theme: 'football', from: '2030-06-08', to: '2030-07-21', name: 'World Cup 2030 (Morocco, Spain, Portugal)' },
];

/** July cycling race editions that do not follow the first-Saturday rule, with their official dates (RG-17). */
export const CYCLING_EXCEPTIONS = {
	2027: { from: '2027-07-02', to: '2027-07-25' },
};

/**
 * The client parameters (Core/Options, application SALFO), read-only in the front-office (RG-21 to RG-24). The names
 * are the ones agreed with the Back-Office; the country has no organisation field in eCOS, hence its own option.
 */
export const SEASONAL_THEME_OPTIONS = {
	enabled: 'SeasonalThemes',
	mode: 'SeasonalThemesMode',
	forced: 'SeasonalThemeForced',
	excluded: 'SeasonalThemesExcluded',
	country: 'SeasonalThemesCountry',
};

const addDays = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
const isoDate = (value) => {
	const [y, m, d] = value.split('-').map(Number);
	return new Date(y, m - 1, d);
};

/** Easter Sunday, Gregorian calendar (Meeus algorithm). */
export function easterSunday(year) {
	const a = year % 19;
	const b = Math.floor(year / 100);
	const c = year % 100;
	const d = Math.floor(b / 4);
	const e = b % 4;
	const f = Math.floor((b + 8) / 25);
	const g = Math.floor((b - f + 1) / 3);
	const h = (19 * a + b - d - g + 15) % 30;
	const i = Math.floor(c / 4);
	const k = c % 4;
	const l = (32 + 2 * e + 2 * i - h - k) % 7;
	const m = Math.floor((a + 11 * h + 22 * l) / 451);
	const month = Math.floor((h + l - 7 * m + 114) / 31);
	const day = ((h + l - 7 * m + 114) % 31) + 1;
	return new Date(year, month - 1, day);
}

/** Carnival: from Fat Thursday (Weiberfastnacht, 52 days before Easter) to Shrove Tuesday (47 days before). */
export const carnivalPeriod = (year) => ({ from: addDays(easterSunday(year), -52), to: addDays(easterSunday(year), -47) });

/** Easter: from 14 days before Easter Sunday to Easter Monday. */
export const easterPeriod = (year) => ({ from: addDays(easterSunday(year), -14), to: addDays(easterSunday(year), 1) });

/** The July cycling race: starts on the first Saturday of July, ends on the Sunday 22 days later. */
export function cyclingPeriod(year) {
	const exception = CYCLING_EXCEPTIONS[year];
	if (exception) return { from: isoDate(exception.from), to: isoDate(exception.to) };
	const july1 = new Date(year, 6, 1);
	const from = addDays(july1, (6 - july1.getDay() + 7) % 7);
	return { from, to: addDays(from, 22) };
}

/**
 * The theme periods of a year for a country ('FR' or 'DE'), by priority: the first period holding a date wins (RG-14).
 * When Valentine's day and carnival overlap, Valentine's day wins in France and carnival in Germany (RG-18).
 */
export function periodsForYear(year, country = 'FR') {
	const de = country === 'DE';
	const D = (month, day) => new Date(year, month, day);
	const period = (theme, { from, to }) => ({ theme, from, to });
	const valentine = period('saint-valentin', { from: D(1, 7), to: D(1, 14) });
	const carnival = period('carnaval', carnivalPeriod(year));
	return [
		...SPORT_EVENTS.filter((event) => isoDate(event.from).getFullYear() === year).map((event) => period(event.theme, { from: isoDate(event.from), to: isoDate(event.to) })),
		...(de ? [] : [period('epiphanie', { from: D(0, 3), to: D(0, 20) })]),
		...(de ? [carnival, valentine] : [valentine, carnival]),
		period('paques', easterPeriod(year)),
		period('cyclisme', cyclingPeriod(year)),
		period('rentree', { from: de ? D(7, 1) : D(7, 20), to: D(8, 15) }),
		period('halloween', { from: D(9, 20), to: D(9, 31) }),
		...(de ? [period('saint-nicolas', { from: D(11, 1), to: D(11, 6) })] : []),
		period('noel', { from: de ? D(11, 7) : D(11, 1), to: D(11, 26) }),
		period('saint-sylvestre', { from: D(11, 27), to: D(11, 31) }),
	];
}

/** The calendar theme of a date for a country ('FR' or 'DE'), or null: neutral shelf (RG-19). */
export function themeForDate(date = new Date(), country = 'FR') {
	const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	return periodsForYear(day.getFullYear(), country).find((p) => day >= p.from && day <= p.to)?.theme ?? null;
}

const optionValue = (options, name) => String((Array.isArray(options) ? options : []).find((o) => o?.name === name && o?.application === 'SALFO')?.value ?? '').trim();

/** The seasonal theme parameters among the client options, with their defaults (disabled, auto, France). */
export function seasonalThemeSettings(options) {
	const value = (key) => optionValue(options, SEASONAL_THEME_OPTIONS[key]);
	return {
		enabled: value('enabled').toLowerCase() === 'true',
		mode: value('mode').toLowerCase() === 'force' ? 'force' : 'auto',
		forced: value('forced').toLowerCase(),
		excluded: value('excluded')
			.toLowerCase()
			.split(/[\s,;]+/)
			.filter(Boolean),
		country: value('country').toUpperCase() === 'DE' ? 'DE' : 'FR',
	};
}

/**
 * The theme the loader shows at a date, or null for the neutral shelf: none unless the client enabled the themes
 * (RG-21), the forced one whatever the date (RG-22) or the calendar one, never an excluded theme (RG-23).
 */
export function resolveSeasonalTheme(settings, date = new Date()) {
	if (!settings?.enabled) return null;
	const theme = settings.mode === 'force' ? settings.forced : themeForDate(date, settings.country);
	return SEASONAL_THEMES.includes(theme) && !settings.excluded.includes(theme) ? theme : null;
}
